import { Box } from "@mui/material";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import MuiAccordionSummary, {
  accordionSummaryClasses,
} from "@mui/material/AccordionSummary";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { IconMinus, IconPlus } from "@tabler/icons-react";
import * as React from "react";
import Text from "../../../../components/ui/Text";

const questions = [
  {
    question: "What sports does it support?",
    answer: (
      <p>
        Findaside supports <b>every sport</b> — from football and basketball to
        ultimate frisbee and padel. You can select from our list of common
        sports, or if yours isn`t listed, you can add your own when creating a
        game.
      </p>
    ),
  },
  {
    question: "How do payments work?",
    answer: (
      <>
        <p>We partner with Stripe for secure, compliant payment collection.</p>
        <p>
          You can collect payments <b>in-app</b> by setting up a Stripe
          Connected Account — funds are automatically deposited to your bank
          account <b>24 hours after the game</b>.
        </p>
        <p>
          If you prefer, you can also collect payments <b>manually</b>, either
          in cash or using your own payment link.
        </p>
      </>
    ),
  },
  {
    question: "Is there a fee?",
    answer: (
      <>
        <p>
          When collecting payments in-app, we charge a <b>small per-player</b>{" "}
          fee that covers Stripe's processing costs and our service fee.
        </p>
        <p>
          This helps us keep Findaside running smoothly and continue improving
          your experience.
        </p>
      </>
    ),
  },
  {
    question: "Can I cancel a game?",
    answer: (
      <>
        <p>Yes, you can cancel a game at any time.</p>
        <p>
          If you’ve collected payments in-app and need to cancel — for example,
          due to bad weather or not enough players — all paid players will
          receive a <b>full refund in game credits</b>.
        </p>
        <p>
          Credits can be <b>used towards their next booking</b> on Findaside.
        </p>
      </>
    ),
  },
  {
    question: "Do I need to know the players?",
    answer: (
      <>
        <p>Not necessarily.</p>
        <p>
          <b>Public games</b> can be joined by anyone in the Findaside
          community.
        </p>
        <p>
          We recommend starting with your existing group (for example, your
          WhatsApp team) and using Findaside to <b>fill extra spots</b> when
          numbers are low.
        </p>
      </>
    ),
  },
];

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  color: "#21272A",
  border: `1px solid #DDE1E6`,
  marginBottom: 16,
  "&:last-child": {
    marginBottom: 0,
  },
  "&::before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props) => {
  const { expanded, ...other } = props;
  return (
    <MuiAccordionSummary
      expandIcon={
        expanded ? (
          <IconMinus
            style={{
              color: "#21272A",
              transform: "rotate(90deg)",
            }}
          />
        ) : (
          <IconPlus
            style={{
              color: "#21272A",
            }}
          />
        )
      }
      {...other}
    />
  );
})(({ theme }) => ({
  backgroundColor: "#ffffff",
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
      transform: "rotate(90deg)",
    },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

export const Questions = () => {
  const [expanded, setExpanded] = React.useState("");

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  return (
    <Box sx={{ backgroundColor: "#F2F4F8", py: { xs: 6, md: 8 }, px: 2 }}>
      <Box sx={{ maxWidth: 900, mx: "auto" }}>
        <Text
          component="h2"
          sx={{
            mb: { xs: 4, md: 8 },
            fontSize: { xs: "28px", sm: "5.5vw", md: "42px" },
            fontWeight: 900,
            color: "#171717",
            lineHeight: "100%",
            textAlign: "center",
          }}
        >
          Frequently asked questions
        </Text>

        {questions.map((item, index) => (
          <Accordion
            key={index}
            expanded={expanded === `panel${index + 1}`}
            onChange={handleChange(`panel${index + 1}`)}
          >
            <AccordionSummary
              expanded={expanded === `panel${index + 1}`}
              aria-controls={`panel${index + 1}d-content`}
              id={`panel${index + 1}d-header`}
            >
              <Typography
                component="span"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 18, md: 20 },
                }}
              >
                {item.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ py: 0 }}>{item.answer}</AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};
